import type { IdentifierDisplayStatus } from '@myplayer/schema';
import { IconAlertCircle, IconCheck, IconClock, IconRecall } from '@myplayer/ui';
import { type ReactElement } from 'react';
import { Badge } from './components/ui/primitives.js';
import type { IdentifierLifecycleState } from './products-firestore.js';
import type { PlainProductStatus } from './products-helpers.js';

export function StatusBadge({
  status,
  lifecycleState,
  plainStatus,
  recalled = false,
  isCheckingPromotion = false,
  pendingFired = false,
  mode = 'table',
}: {
  readonly status: IdentifierDisplayStatus;
  readonly lifecycleState?: IdentifierLifecycleState | undefined;
  readonly plainStatus?: PlainProductStatus | undefined;
  readonly recalled?: boolean;
  readonly isCheckingPromotion?: boolean;
  readonly pendingFired?: boolean;
  readonly mode?: 'table' | 'detail';
}): ReactElement {
  if (recalled) {
    return (
      <Badge data-testid="status-badge-recalled" variant="destructive" className="gap-1">
        <IconRecall />
        <span>Needs attention · Recalled</span>
      </Badge>
    );
  }
  if (status === 'invalid' || lifecycleState === 'invalid' || plainStatus === 'NEEDS_ATTENTION') {
    return (
      <Badge data-testid="status-badge-invalid" variant="destructive" className="gap-1">
        <IconAlertCircle />
        <span>Needs attention</span>
      </Badge>
    );
  }
  if (pendingFired) {
    return (
      <Badge data-testid="status-badge-switching" variant="warning" className="gap-1">
        <IconClock />
        <span>Switching now…</span>
      </Badge>
    );
  }
  if (isCheckingPromotion) {
    return (
      <Badge data-testid="status-badge-checking" variant="warning" className="gap-1">
        <IconClock />
        <span>Checking…</span>
      </Badge>
    );
  }
  if (
    (mode === 'table' && plainStatus === 'SCHEDULED') ||
    (plainStatus === 'SCHEDULED' && lifecycleState !== 'draft' && lifecycleState !== 'pending')
  ) {
    return (
      <Badge data-testid="status-badge-scheduled" variant="warning" className="gap-1">
        <IconClock />
        <span>Scheduled</span>
      </Badge>
    );
  }
  if (lifecycleState === 'draft' || lifecycleState === 'pending') {
    return (
      <span data-testid="status-badge-pending" className="inline-flex">
        <Badge data-testid="status-badge-unpublished" variant="warning" className="gap-1">
          <IconClock />
          <span>Has unpublished changes</span>
        </Badge>
      </span>
    );
  }
  return (
    <Badge data-testid="status-badge-valid" variant="success" className="gap-1">
      <IconCheck />
      <span data-testid="status-badge-live">{mode === 'detail' ? 'Live now' : 'Live'}</span>
    </Badge>
  );
}
